/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { AppConfig } from '@/types/config';
import type { TrackingConsent } from '@/types/tracking-consent';

/**
 * Consent Mode v2 storage keys the storefront's binary consent controls.
 *
 * `security_storage` and `functionality_storage` are intentionally absent — they cover
 * storage required for the site to work at all, are not gated by the banner, and default
 * to granted in Consent Mode.
 */
export const GATED_CONSENT_KEYS = [
    'analytics_storage',
    'ad_storage',
    'ad_user_data',
    'ad_personalization',
    'personalization_storage',
] as const;

/**
 * How long GTM holds gated tags waiting for a `consent update`.
 *
 * Only emitted when the shopper's choice is *not* known at render time. A known choice is
 * asserted as the default outright, so there is nothing to wait for.
 */
export const CONSENT_WAIT_FOR_UPDATE_MS = 500;

/**
 * Consent Mode payload covering every gated key.
 *
 * Shared by the snippet's `default` and the bridge's `update` so the two cannot drift.
 */
export const consentState = (granted: boolean): Record<string, 'granted' | 'denied'> =>
    Object.fromEntries(GATED_CONSENT_KEYS.map((k) => [k, granted ? 'granted' : 'denied']));

/**
 * The shopper's consent choice as known at render time, or `undefined` when it is not known.
 *
 * Consent lives in the SLAS token, so the root loader already knows it server-side — this
 * lets `<GoogleTagManagerScript>` assert it as the Consent Mode *default* rather than
 * starting denied and waiting for `<GoogleTagManagerConsentBridge>` to correct it after
 * hydration. On a slow client the `wait_for_update` window closes first, so without this an
 * already-consented shopper's first pageview ships as denied on every full page load.
 *
 * The `enabled` read deliberately mirrors `useTrackingConsent`'s own (a plain truthy check)
 * rather than the stricter explicit-opt-in guard `container-id.ts` applies to
 * `googleTagManager.enabled`. The two readings must agree: this resolver sets the snippet's
 * defaults and the bridge later pushes an update derived from the hook, so a stricter reading
 * here would let them disagree about whether the feature is on at all.
 *
 * Feature off resolves to `undefined`, i.e. denied defaults — a storefront that shows no
 * banner never silently grants.
 *
 * @param trackingConsentConfig - the resolved `engagement.analytics.trackingConsent` block
 * @param trackingConsent - the shopper's stored choice, from `clientAuth`
 */
export function resolveGtmInitialConsent(
    trackingConsentConfig: AppConfig['engagement']['analytics']['trackingConsent'] | undefined,
    trackingConsent: TrackingConsent | undefined
): TrackingConsent | undefined {
    if (!(trackingConsentConfig?.enabled ?? false)) return undefined;
    return trackingConsent;
}
